import { useState } from 'react';
import clsx from 'clsx';
import { useQuery } from '@tanstack/react-query';
import {
  AlertTriangle,
  ClipboardList,
  Clock,
  Crown,
  Eye,
  Flame,
  ListOrdered,
  Megaphone,
  ShieldAlert,
  Ticket,
  Users,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { api, type WorkMode } from '../lib/api';
import { P, useAuth } from '../lib/auth';
import { enumLabel, useI18n, type TranslationKey } from '../lib/i18n';
import {
  formatDate,
  formatDays,
  formatMinutes,
  formatMoney,
  formatNumber,
  formatPercent,
  formatShortTime,
  formatTime,
} from '../lib/format';
import { SlaCountdown } from '../components/SlaCountdown';
import { TaskHolder, type HeldTask } from '../components/TaskHolder';
import { AnnouncementItem } from './Announcements';
import { MonitorBanner } from '../components/MonitorBanner';
import { MonitoringTiles, useMonitoringOverview } from '../components/MonitoringTiles';
import { WORK_MODE_ICONS } from '../components/WorkModeLabel';
import { PersonWorkDialog, type PersonOnShift } from '../components/PersonWork';
import { FeatureTips } from '../components/FeatureTips';
import { WorkProgressCard } from '../components/WorkProgress';
import { WorkHoursCard } from '../components/WorkHours';
import {
  Badge,
  Card,
  CardHeader,
  EmptyState,
  ErrorState,
  LoadingBlock,
  Metric,
  PageHeader,
  ProgressBar,
  StatusDot,
  Table,
  Td,
  Th,
  Tr,
  priorityTone,
  statusTone,
} from '../components/ui';

/** §29 — the dashboard is assembled from whichever blocks the API returns non-null. */
export function DashboardPage() {
  const { t, locale, localized } = useI18n();
  const { user, can } = useAuth();
  // Monitoring's six figures, above Company, for whoever may open Monitoring — the administrators.
  const monitoring = useMonitoringOverview(can(P.MONITORING_MANAGE));

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['dashboard'],
    queryFn: () => api.get<any>('/dashboard'),
    // SLA countdowns go stale fast; re-pull the server's numbers every half minute.
    refetchInterval: 30_000,
  });

  if (isLoading) return <LoadingBlock />;
  if (error) return <ErrorState error={error} onRetry={() => void refetch()} />;

  // The finance block (§29.5) is sys's home page now; this is the operations view.
  const { employee, teamMember, teamLeader, admin, announcements, workingNow, urgentTasks, progress, hours } = data ?? {};

  return (
    <>
      <PageHeader
        title={t('dash.title')}
        subtitle={`${user?.fullName} · ${user?.roles.map((r) => (locale === 'ar' ? r.nameAr ?? r.name : r.name)).join(', ')}`}
      />

      {/* ---- The reader's own progress through their work today — everyone's (2026-11-02) ---- */}
      {progress && <WorkProgressCard block={progress} />}
      {/* ---- And their hours against what the day owes — everyone's (2026-11-02) ---- */}
      {hours && <WorkHoursCard block={hours} />}

      {/* ---- What's new: tips about features lately shipped that this person may open ---- */}
      <FeatureTips />

      {/* ---- Announcements this person has not opened yet; opened, they leave ---- */}
      {announcements?.latest?.length > 0 && (
        <section className="mb-8">
          <Card padded={false}>
            <CardHeader
              className="p-5 pb-0"
              title={
                <span className="flex items-center gap-2">
                  <Megaphone className="h-5 w-5 shrink-0 text-primary-600 dark:text-primary-400 flip-rtl" aria-hidden />
                  {t('dash.announcements')}
                  {announcements.unread > 0 && (
                    <Badge tone="brand" className="tabular">
                      {t('dash.unreadN', { n: formatNumber(announcements.unread, locale) })}
                    </Badge>
                  )}
                </span>
              }
              action={
                <Link to="/announcements" className="text-body-sm font-medium text-primary-600 dark:text-primary-400 hover:underline">
                  {t('dash.viewAll')}
                </Link>
              }
            />
            <ul>
              {announcements.latest.map((announcement: any) => (
                <AnnouncementItem key={announcement.id} announcement={announcement} />
              ))}
            </ul>
          </Card>
        </section>
      )}

      {/* ---- Leaders Tasks asked of this leader and still open ---------------- */}
      {employee?.myLeaderTasks?.open > 0 && (
        <section className="mb-8">
          <Card>
            <div className="flex flex-wrap items-center gap-4">
              <Crown className="h-6 w-6 shrink-0 text-primary-600 dark:text-primary-400" aria-hidden />
              <div className="min-w-0 flex-1">
                <p className="text-title text-[var(--text-primary)]">{t('dash.leaderTasks')}</p>
                <p className="mt-0.5 flex flex-wrap items-center gap-2 text-body-sm text-muted">
                  {t('dash.leaderTasksOpen', { n: formatNumber(employee.myLeaderTasks.open, locale) })}
                  {employee.myLeaderTasks.review > 0 && (
                    <Badge tone="success" className="tabular">
                      {t('dash.leaderTasksReview', { n: formatNumber(employee.myLeaderTasks.review, locale) })}
                    </Badge>
                  )}
                </p>
              </div>
              <Link to="/leader-tasks" className="text-body-sm font-medium text-primary-600 dark:text-primary-400 hover:underline">
                {t('dash.viewAll')}
              </Link>
            </div>
          </Card>
        </section>
      )}

      {/* ---- The reader's own urgent tasks still open, whatever their role ---- */}
      {urgentTasks?.total > 0 && <UrgentTasksCard block={urgentTasks} />}

      {/* ---- Employee block (§29.1) — everyone sees this --------------------- */}
      {employee && (
        <section className="mb-8">
          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
            <Metric
              label={t('dash.myTasks')}
              value={formatNumber(employee.myTasks, locale)}
              icon={<ClipboardList className="h-4 w-4" />}
            />
            <Metric
              label={t('dash.myOverdue')}
              value={formatNumber(employee.myOverdueTasks, locale)}
              tone={employee.myOverdueTasks > 0 ? 'error' : 'neutral'}
              icon={<AlertTriangle className="h-4 w-4" />}
            />
            <Metric
              label={t('dash.myTickets')}
              value={formatNumber(employee.myOpenTickets, locale)}
              icon={<Ticket className="h-4 w-4" />}
            />
            <Metric
              label={t('dash.myPenalties')}
              value={formatDays(employee.myPenaltiesThisMonth.days ?? 0, locale)}
              hint={
                employee.myPenaltiesThisMonth.total > 0
                  ? `${employee.myPenaltiesThisMonth.count} × · ≈ ${formatMoney(employee.myPenaltiesThisMonth.total, 'EGP', locale)}`
                  : `${employee.myPenaltiesThisMonth.count} ×`
              }
              tone={employee.myPenaltiesThisMonth.total > 0 ? 'warning' : 'neutral'}
              icon={<ShieldAlert className="h-4 w-4" />}
            />
          </div>

          <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-2">
            <Card>
              <CardHeader title={t('dash.currentShift')} />
              {employee.currentShift?.onShift ? (
                <div className="flex items-center gap-3">
                  <StatusDot tone="online" pulse />
                  <div>
                    <p className="text-title text-[var(--text-primary)]">
                      {enumLabel(employee.currentShift.shiftType, locale)}
                    </p>
                    <p className="text-body-sm text-muted">{t('shifts.onShiftNow')}</p>
                  </div>
                </div>
              ) : (
                <div className="flex items-center gap-3">
                  <StatusDot tone="offline" />
                  <div>
                    <p className="text-title text-[var(--text-primary)]">{t('dash.notOnShift')}</p>
                    <p className="text-body-sm text-muted">
                      {enumLabel(employee.currentShift?.reason ?? null, locale)}
                    </p>
                  </div>
                </div>
              )}
            </Card>

            <Card>
              <CardHeader title={t('dash.nextShift')} />
              {employee.nextShift ? (
                <div className="flex items-center gap-3">
                  <Clock className="h-5 w-5 text-[var(--text-tertiary)]" aria-hidden />
                  <div>
                    <p className="text-title text-[var(--text-primary)]">
                      {formatDate(employee.nextShift.date, locale)} ·{' '}
                      {enumLabel(employee.nextShift.type, locale)}
                    </p>
                    <p className="text-body-sm text-muted tabular">
                      {employee.nextShift.startAt
                        ? `${formatTime(employee.nextShift.startAt, locale)} — ${formatTime(employee.nextShift.endAt, locale)}`
                        : '—'}
                    </p>
                  </div>
                </div>
              ) : (
                <p className="text-body-sm text-muted">{t('common.none')}</p>
              )}
            </Card>
          </div>
        </section>
      )}

      {/* ---- Who is working right now — team leaders and admins --------------- */}
      {workingNow && <WorkingNowCard block={workingNow} />}

      {/* ---- Team member block (§29.2) --------------------------------------- */}
      {teamMember && (
        <section className="mb-8">
          <h2 className="mb-3 text-h2 text-[var(--text-primary)]">{t('dash.teamMember')}</h2>
          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
            <Metric
              label={t('dash.awaitingMyView')}
              value={formatNumber(teamMember.awaitingMyView, locale)}
              tone={teamMember.awaitingMyView > 0 ? 'error' : 'success'}
              icon={<Eye className="h-4 w-4" />}
            />
            <Metric
              label={t('dash.unassignedTickets')}
              value={formatNumber(teamMember.unassignedDepartmentTickets, locale)}
              tone={teamMember.unassignedDepartmentTickets > 0 ? 'warning' : 'neutral'}
            />
            <Metric label={t('dash.myAssigned')} value={formatNumber(teamMember.myAssignedTickets, locale)} />
            <Metric
              label={t('dash.availableQTasks')}
              value={formatNumber(teamMember.availableQTasks?.length ?? 0, locale)}
              icon={<ListOrdered className="h-4 w-4" />}
            />
          </div>

          {/* grid-cols-1, not the implicit column: that one lets a card grow to its widest
              row, and the SLA table pushed a phone's whole page sideways (2026-10-10). */}
          <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-2">
            {/* §37.3 — server timestamps drive these; the client only ticks them. */}
            <Card padded={false}>
              <CardHeader title={t('dash.slaCountdowns')} className="p-5 pb-0" />
              {teamMember.slaCountdowns?.length ? (
                <Table>
                  <tbody>
                    {teamMember.slaCountdowns.map((ticket: any) => (
                      <tr key={ticket.id} className="border-b hairline last:border-0">
                        {/* w-full max-w-0: the ticket takes what the clocks leave, and its
                            subject truncates instead of widening the table. The gap between
                            the two is narrow, so a phone still shows the whole number. */}
                        <td className="w-full max-w-0 py-2.5 pe-2 ps-5">
                          <Link to={`/tickets/${ticket.id}`} className="block min-w-0">
                            <span className="block truncate font-mono text-mono-sm text-primary-600 dark:text-primary-400">{ticket.number}</span>
                            <span className="block truncate text-body-sm text-[var(--text-primary)]">
                              {ticket.subject}
                            </span>
                          </Link>
                        </td>
                        <td className="py-2.5 pe-5 ps-2 text-end">
                          <div className="inline-flex flex-col items-end gap-1">
                            <SlaCountdown
                              secondsRemaining={ticket.viewSecondsRemaining}
                              measuredAt={teamMember.serverNow}
                              label={t('tickets.viewSla')}
                              met={ticket.viewMet}
                              size="sm"
                            />
                            <SlaCountdown
                              secondsRemaining={ticket.assignmentSecondsRemaining}
                              measuredAt={teamMember.serverNow}
                              label={t('tickets.assignmentSla')}
                              size="sm"
                            />
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              ) : (
                <EmptyState title={t('dash.nothingPending')} hint="" />
              )}
            </Card>

            <Card>
              <CardHeader title={t('dash.currentQTask')} />
              {teamMember.currentQTask ? (
                <Link to={`/q-tasks/${teamMember.currentQTask.id}`} className="block">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <span className="font-mono text-mono-sm text-primary-600 dark:text-primary-400">
                        {teamMember.currentQTask.number}
                      </span>
                      <p className="truncate text-title text-[var(--text-primary)]">
                        {teamMember.currentQTask.title}
                      </p>
                      <p className="text-body-sm text-muted">
                        {localized(teamMember.currentQTask.department)}
                      </p>
                    </div>
                    <Badge tone={statusTone(teamMember.currentQTask.status)}>
                      {enumLabel(teamMember.currentQTask.status, locale)}
                    </Badge>
                  </div>
                </Link>
              ) : teamMember.availableQTasks?.length ? (
                <ul className="space-y-2">
                  {teamMember.availableQTasks.map((q: any) => (
                    <li key={q.id}>
                      <Link
                        to={`/q-tasks/${q.id}`}
                        className="flex items-center justify-between gap-3 rounded-md border hairline px-3 py-2 hover:border-primary/50"
                      >
                        <span className="min-w-0">
                          <span className="font-mono text-mono-sm text-primary-600 dark:text-primary-400">{q.number}</span>
                          <span className="block truncate text-body-sm">{q.title}</span>
                        </span>
                        <Badge tone="info">{enumLabel(q.status, locale)}</Badge>
                      </Link>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-body-sm text-muted">{t('common.none')}</p>
              )}
            </Card>
          </div>
        </section>
      )}

      {/* ---- Team leader block (§29.3) --------------------------------------- */}
      {teamLeader && (
        <section className="mb-8">
          <h2 className="mb-3 text-h2 text-[var(--text-primary)]">{t('dash.teamLeader')}</h2>
          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
            <Metric
              label={t('common.unassigned')}
              value={formatNumber(teamLeader.unassigned, locale)}
              tone={teamLeader.unassigned > 0 ? 'warning' : 'success'}
            />
            <Metric
              label={t('dash.urgent')}
              value={formatNumber(teamLeader.urgent, locale)}
              tone={teamLeader.urgent > 0 ? 'error' : 'neutral'}
            />
            <Metric
              label={t('dash.viewBreaches')}
              value={formatNumber(teamLeader.viewBreaches, locale)}
              tone={teamLeader.viewBreaches > 0 ? 'error' : 'success'}
            />
            <Metric
              label={t('dash.assignmentBreaches')}
              value={formatNumber(teamLeader.assignmentBreaches, locale)}
              tone={teamLeader.assignmentBreaches > 0 ? 'error' : 'success'}
            />
          </div>

          <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-2">
            <Card padded={false}>
              {/* Who is working is "Working now" above; the dot here says the same. */}
              <CardHeader title={t('dash.departmentWorkload')} className="p-5 pb-0" />
              <Table>
                <thead>
                  <tr>
                    <Th>{t('common.name')}</Th>
                    <Th>{t('common.status')}</Th>
                    <Th align="end">{t('nav.tickets')}</Th>
                  </tr>
                </thead>
                <tbody>
                  {teamLeader.staff.map((row: any) => (
                    <Tr key={row.user.id}>
                      <Td>
                        <span className="flex items-center gap-2">
                          <StatusDot
                            tone={row.attendance === 'WORKING' ? 'online' : row.attendance === 'ON_BREAK' ? 'idle' : 'offline'}
                            pulse={row.attendance === 'WORKING'}
                          />
                          <span className="truncate">{localized(row.user)}</span>
                          {row.memberType === 'LEADER' && (
                            <Badge tone="brand" className="!px-1.5 !py-0">
                              {t('departments.leaders')}
                            </Badge>
                          )}
                        </span>
                      </Td>
                      <Td>
                        <span className="text-body-sm text-muted">
                          {row.onShift ? enumLabel(row.shiftType, locale) : enumLabel(row.reason, locale)}
                        </span>
                      </Td>
                      <Td align="end" className="tabular">
                        {formatNumber(row.openTickets, locale)}
                      </Td>
                    </Tr>
                  ))}
                </tbody>
              </Table>
            </Card>

            <Card padded={false}>
              <CardHeader
                title={t('dash.qTaskQueue')}
                subtitle={
                  teamLeader.blockedQTasks > 0
                    ? `${teamLeader.blockedQTasks} ${t('dash.blockedQTasks').toLowerCase()}`
                    : undefined
                }
                className="p-5 pb-0"
              />
              {teamLeader.qTaskQueue?.length ? (
                <ul>
                  {teamLeader.qTaskQueue.slice(0, 8).map((q: any, index: number) => (
                    <li key={q.id} className="border-b hairline last:border-0">
                      <Link
                        to={`/q-tasks/${q.id}`}
                        className="flex items-center gap-3 px-5 py-2.5 hover:bg-[var(--surface-sunken)]"
                      >
                        <span className="w-6 shrink-0 text-center font-mono text-mono-sm text-[var(--text-tertiary)] tabular">
                          #{index + 1}
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className="block truncate text-body-sm text-[var(--text-primary)]">{q.title}</span>
                          {q.assignee && (
                            <span className="block truncate text-caption text-muted">
                              {localized(q.assignee)}
                            </span>
                          )}
                        </span>
                        <Badge tone={statusTone(q.status)}>{enumLabel(q.status, locale)}</Badge>
                      </Link>
                    </li>
                  ))}
                </ul>
              ) : (
                <EmptyState title={t('common.none')} hint="" />
              )}
            </Card>
          </div>
        </section>
      )}

      {/* ---- Monitoring — the Overview's six figures (operator, 2026-10-20) ---- */}
      {monitoring.data && (
        <section className="mb-8">
          <div className="mb-3 flex flex-wrap items-baseline justify-between gap-2">
            <h2 className="text-h2 text-[var(--text-primary)]">{t('nav.groupMonitoring')}</h2>
            <Link to="/monitoring" className="text-body-sm font-medium text-primary-600 dark:text-primary-400 hover:underline">
              {t('dash.openMonitoring')}
            </Link>
          </div>
          <MonitorBanner />
          <MonitoringTiles data={monitoring.data} />
        </section>
      )}

      {/* ---- Admin block (§29.4) --------------------------------------------- */}
      {admin && (
        <section className="mb-8">
          <h2 className="mb-3 text-h2 text-[var(--text-primary)]">{t('dash.admin')}</h2>
          <div className="grid gap-4 sm:grid-cols-3">
            <Metric
              label={t('dash.slaCompliance')}
              value={formatPercent(admin.slaCompliance.assignmentCompliancePercent, locale)}
              hint={`${admin.slaCompliance.assignedOnTime} / ${admin.slaCompliance.totalTickets}`}
              tone={
                admin.slaCompliance.assignmentCompliancePercent >= 90
                  ? 'success'
                  : admin.slaCompliance.assignmentCompliancePercent >= 70
                    ? 'warning'
                    : 'error'
              }
            />
            <Metric
              label={t('dash.activeDepartments')}
              value={formatNumber(admin.activeDepartments, locale)}
              icon={<Users className="h-4 w-4" />}
            />
            <Metric label={t('dash.activeProjects')} value={formatNumber(admin.activeProjects, locale)} />
          </div>

          <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-2">
            <Card>
              <CardHeader title={t('dash.ticketsByStatus')} />
              <StatusBars counts={admin.ticketsByStatus} />
            </Card>
            <Card>
              <CardHeader title={t('dash.tasksByStatus')} />
              <StatusBars counts={admin.tasksByStatus} />
            </Card>
          </div>
        </section>
      )}

    </>
  );
}

interface UrgentTask extends HeldTask {
  id: string;
  title: string;
  status: string;
  dueAt: string | null;
  isOverdue: boolean;
  /** A Leaders Task opens under Leaders Tasks. */
  isLeaderTask: boolean;
  project: { id: string; name: string; nameAr: string | null; code: string } | null;
}

interface UrgentTasksBlock {
  total: number;
  /** The nearest deadlines first, overdue at the top; the API names the first ten. */
  tasks: UrgentTask[];
}

/**
 * The reader's own urgent tasks not finished yet, by name (operator, 2026-10-11:
 * "employees urgent tasks must shown in dashboard"; 2026-10-13: "show only my
 * Urgent tasks not all tasks") — an admin's and a team leader's too. View all
 * opens them on the Tasks list, under My tasks.
 */
function UrgentTasksCard({ block }: { block: UrgentTasksBlock }) {
  const { t, locale, localized } = useI18n();

  return (
    <section className="mb-8">
      <Card padded={false}>
        <CardHeader
          className="p-5 pb-0"
          title={
            <span className="flex items-center gap-2">
              <Flame className="h-5 w-5 shrink-0 text-error-fg dark:text-error-dark" aria-hidden />
              {t('dash.urgentTasks')}
              <Badge tone="error" className="tabular">
                {formatNumber(block.total, locale)}
              </Badge>
            </span>
          }
          subtitle={t('dash.urgentTasksHint')}
          action={
            <Link to="/tasks?priority=URGENT&mine=true" className="text-body-sm font-medium text-primary-600 dark:text-primary-400 hover:underline">
              {t('dash.viewAll')}
            </Link>
          }
        />
        <ul>
          {block.tasks.map((task) => (
            <li key={task.id} className="border-b hairline last:border-0">
              <Link
                to={task.isLeaderTask ? `/leader-tasks/${task.id}` : `/tasks/${task.id}`}
                className="flex items-center gap-3 px-5 py-2.5 hover:bg-[var(--surface-sunken)]"
              >
                <span className="min-w-0 flex-1">
                  <span className="flex min-w-0 items-center gap-2">
                    <span className="truncate text-body-sm font-medium text-[var(--text-primary)]" dir="auto">
                      {task.title}
                    </span>
                    {task.isLeaderTask && (
                      <Badge tone="brand" className="!px-1.5 !py-0">
                        {t('leaderTasks.badge')}
                      </Badge>
                    )}
                  </span>
                  <span className="mt-0.5 flex min-w-0 items-center gap-1.5 text-caption text-muted">
                    <TaskHolder task={task} icon className="max-w-[60%]" />
                    <span aria-hidden>·</span>
                    <span className="truncate">{task.project ? localized(task.project) : t('common.general')}</span>
                  </span>
                </span>
                <span className="flex shrink-0 flex-col items-end gap-1">
                  <Badge tone={statusTone(task.status)}>{enumLabel(task.status, locale)}</Badge>
                  <span
                    className={clsx(
                      'inline-flex items-center gap-1 text-caption tabular',
                      task.isOverdue ? 'text-error-fg dark:text-error-dark' : 'text-muted',
                    )}
                  >
                    {task.isOverdue && (
                      <>
                        <AlertTriangle className="h-3.5 w-3.5" aria-hidden />
                        <span className="sr-only">{t('tickets.overdue')}: </span>
                      </>
                    )}
                    {task.dueAt ? formatDate(task.dueAt, locale) : t('dash.noDeadline')}
                  </span>
                </span>
              </Link>
            </li>
          ))}
        </ul>
      </Card>
    </section>
  );
}

interface WorkingNowRow {
  /**
   * `workMode` is where they work from; an API from before it sends none (2026-10-21). `remoteByRequest`: someone at
   * the office whose remote work is approved for the day — under Remote that day (2026-10-28).
   */
  user: { id: string; fullName: string; fullNameAr: string | null; workMode?: WorkMode; remoteByRequest?: boolean };
  /** When their shift started — or, on a break, when the break did. */
  since: string;
  /**
   * What they have really worked today: the day's shifts less rest breaks and Not
   * working, as the daily-hours rule counts it. An API from before it sends none (2026-10-22).
   */
  workedMinutes?: number;
}

type OnBreakRow = WorkingNowRow & {
  /** `isPause`: they paused work (2026-10-24) — listed with the breaks, and called Paused. */
  breakType: { name: string; nameAr: string | null; maxMinutes: number | null; isPause?: boolean };
  overLimit: boolean;
};
type NotWorkingRow = WorkingNowRow & { reason: string | null };
/** In a Free Shift (CR-006): `workedMinutes` is the day's Regular figure, `freeMinutes` this Free Shift's so far. */
type FreeShiftRow = WorkingNowRow & { freeMinutes: number; notWorking: boolean };

interface WorkingNowBlock {
  working: WorkingNowRow[];
  onBreak: OnBreakRow[];
  /** On shift, but the Desktop Agent shows them away from their computer — and why. Older APIs send none. */
  notWorking?: NotWorkingRow[];
  /** After their Regular Shift, in a Free Shift — unpaid, never on shift. Older APIs send none. */
  freeShift?: FreeShiftRow[];
}

/** Names shown in each list before "Show all": a couple of rows in half the card. */
const WORKING_SHOWN = 12;

/** The card's two halves: the office first, then remote. */
const WORK_MODE_HALVES: Array<{ mode: WorkMode; title: TranslationKey; nobody: TranslationKey }> = [
  { mode: 'OFFICE', title: 'dash.workingOffice', nobody: 'dash.nobodyOffice' },
  { mode: 'REMOTE', title: 'dash.workingRemote', nobody: 'dash.nobodyRemote' },
];

/**
 * Who is working right now, by name, and who is on a break — a glance for a
 * team leader or an admin, those at the office and those remote apart
 * (operator, 2026-10-21: "in dashboard i want to sperate office employees to
 * remotely and who is working and who is in break"). Each half lists Working,
 * On a break and, when the Desktop Agent says so, Not working. The attendance
 * board has the whole day. A name opens what that person is working on
 * (operator, 2026-10-27).
 */
function WorkingNowCard({ block }: { block: WorkingNowBlock }) {
  const { t, locale, localized } = useI18n();
  const [all, setAll] = useState(false);
  const [person, setPerson] = useState<PersonOnShift | null>(null);
  const n = (value: number) => formatNumber(value, locale);
  const byName = (a: WorkingNowRow, b: WorkingNowRow) => localized(a.user).localeCompare(localized(b.user), locale);

  const notWorking = block.notWorking ?? [];
  const freeShift = block.freeShift ?? [];
  const total = block.working.length + block.onBreak.length + notWorking.length + freeShift.length;
  // Each list by name, in the reader's language. Someone the API sends without a
  // work mode is at the office, as the column's default makes everyone.
  const from = <T extends WorkingNowRow>(rows: T[], mode: WorkMode) =>
    rows.filter((row) => (row.user.workMode ?? 'OFFICE') === mode).sort(byName);
  const halves = WORK_MODE_HALVES.map((half) => ({
    ...half,
    working: from(block.working, half.mode),
    onBreak: from(block.onBreak, half.mode),
    notWorking: from(notWorking, half.mode),
    freeShift: from(freeShift, half.mode),
  }));
  // One switch opens every list cut short.
  const cut = halves.some((half) =>
    [half.working, half.onBreak, half.notWorking, half.freeShift].some((rows) => rows.length > WORKING_SHOWN),
  );

  return (
    <section className="mb-8">
      <Card>
        <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="text-h3 text-[var(--text-primary)]">{t('dash.workingNow')}</h2>
            <Badge tone={block.working.length > 0 ? 'success' : 'neutral'} className="tabular">
              {n(block.working.length)}
            </Badge>
            {block.onBreak.length > 0 && (
              <Badge tone="warning" className="tabular">
                {t('dash.onBreakN', { n: n(block.onBreak.length) })}
              </Badge>
            )}
            {notWorking.length > 0 && (
              <Badge tone="error" className="tabular">
                {t('dash.notWorkingN', { n: n(notWorking.length) })}
              </Badge>
            )}
            {freeShift.length > 0 && (
              <Badge tone="info" className="tabular">
                {t('attendance.freeShiftNow', { n: n(freeShift.length) })}
              </Badge>
            )}
          </div>
          <Link to="/attendance?tab=team" className="text-body-sm font-medium text-primary-600 dark:text-primary-400 hover:underline">
            {t('dash.teamAttendance')}
          </Link>
        </div>

        {total === 0 ? (
          <p className="mt-3 text-body-sm text-muted">{t('dash.nobodyWorking')}</p>
        ) : (
          // grid-cols-1, not the implicit column: a long name must not widen a phone's page.
          <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-2">
            {halves.map((half) => (
              <WorkModeHalf key={half.mode} half={half} all={all} onOpen={setPerson} />
            ))}
          </div>
        )}
        {cut && (
          <button
            type="button"
            onClick={() => setAll((open) => !open)}
            className="mt-3 text-caption font-medium text-primary-600 dark:text-primary-400 hover:underline"
          >
            {all ? t('announcements.showLess') : t('announcements.showAll', { n: n(total) })}
          </button>
        )}
      </Card>
      {person && <PersonWorkDialog person={person} onClose={() => setPerson(null)} />}
    </section>
  );
}

/** Half of Working now: the office's people, or remote's — working, on a break, Not working. */
function WorkModeHalf({
  half,
  all,
  onOpen,
}: {
  half: (typeof WORK_MODE_HALVES)[number] & {
    working: WorkingNowRow[];
    onBreak: OnBreakRow[];
    notWorking: NotWorkingRow[];
    freeShift: FreeShiftRow[];
  };
  all: boolean;
  onOpen: (person: PersonOnShift) => void;
}) {
  const { t, locale, localized } = useI18n();
  const Icon = WORK_MODE_ICONS[half.mode];
  const onShift = half.working.length + half.onBreak.length + half.notWorking.length;

  return (
    <div className="min-w-0 rounded-md border hairline p-4">
      <h3 className="flex flex-wrap items-center gap-x-2 gap-y-1">
        <Icon className="h-4 w-4 shrink-0 text-[var(--text-tertiary)]" aria-hidden />
        <span className="text-title text-[var(--text-primary)]">{t(half.title)}</span>
        <span className="text-body-sm tabular text-muted">{t('dash.onShiftN', { n: formatNumber(onShift, locale) })}</span>
      </h3>
      {onShift === 0 && half.freeShift.length === 0 ? (
        <p className="mt-2 text-body-sm text-muted">{t(half.nobody)}</p>
      ) : (
        <dl className="mt-3 space-y-3">
          <PeopleList
            label={t('attendance.status.WORKING')}
            dot="online"
            rows={half.working}
            all={all}
            onOpen={onOpen}
            doing={() => t('attendance.status.WORKING')}
          />
          <PeopleList
            label={t('attendance.status.ON_BREAK')}
            dot="idle"
            rows={half.onBreak}
            all={all}
            onOpen={onOpen}
            note={(row) => ({ text: row.breakType.isPause ? t('attendance.paused') : localized(row.breakType), alert: row.overLimit })}
            doing={(row) => {
              const type = row.breakType.isPause ? t('attendance.paused') : localized(row.breakType);
              return row.overLimit ? t('violations.breakOver', { type }) : type;
            }}
          />
          {/* Only while the Desktop Agent shows someone away: most of the time there is nobody to list. */}
          {half.notWorking.length > 0 && (
            <PeopleList
              label={t('presence.NOT_WORKING')}
              dot="alert"
              rows={half.notWorking}
              all={all}
              onOpen={onOpen}
              note={(row) => ({ text: awayReason(row, t), alert: true })}
              doing={(row) => `${t('presence.NOT_WORKING')} — ${awayReason(row, t)}`}
            />
          )}
          {/* After their Regular Shift (CR-006): unpaid, tracked — not on shift, so listed last and only when someone is. */}
          {half.freeShift.length > 0 && (
            <PeopleList
              label={t('attendance.freeShift')}
              dot={half.freeShift.every((row) => row.notWorking) ? 'alert' : 'online'}
              rows={half.freeShift}
              all={all}
              onOpen={onOpen}
              note={(row) => ({
                text: row.notWorking ? t('presence.NOT_WORKING') : t('attendance.freeMinutesShort', { minutes: formatMinutes(row.freeMinutes, locale) }),
                alert: row.notWorking,
              })}
              doing={() => t('attendance.inFreeShift')}
            />
          )}
        </dl>
      )}
    </div>
  );
}

/** Why the Desktop Agent shows someone Not working: idle, locked, asleep, the agent gone. */
const awayReason = (row: NotWorkingRow, t: ReturnType<typeof useI18n>['t']) =>
  row.reason ? t(`presence.reason.${row.reason}` as TranslationKey) : t('presence.NOT_WORKING');

/**
 * One list of a half, under its label and count: names as chips, a dot for
 * what they are doing, the break or the reason after the name, the hours they
 * have really worked under it, and since when on hover. Cut at WORKING_SHOWN
 * names until the card shows them all. A chip opens what that person is working
 * on (2026-10-27).
 */
function PeopleList<T extends WorkingNowRow>({
  label,
  dot,
  rows,
  all,
  onOpen,
  note,
  doing,
}: {
  label: string;
  dot: 'online' | 'idle' | 'alert';
  rows: T[];
  all: boolean;
  onOpen: (person: PersonOnShift) => void;
  /** What follows the name — the break, or why they are away — and whether it is a warning. */
  note?: (row: T) => { text: string; alert: boolean };
  /** What they are doing, in words: Working, the break (over its limit), why they are away. */
  doing: (row: T) => string;
}) {
  const { t, locale, localized } = useI18n();
  const shown = all ? rows : rows.slice(0, WORKING_SHOWN);
  const more = rows.length - shown.length;
  const worked = (row: T) => `${t('attendance.worked')} ${formatMinutes(row.workedMinutes, locale)}`;
  const title = (row: T) =>
    `${doing(row)} · ${t('attendance.since', { time: formatShortTime(row.since, locale) })}${
      row.user.remoteByRequest ? ` · ${t('remoteWork.todayByRequest')}` : ''
    }`;
  const RemoteIcon = WORK_MODE_ICONS.REMOTE;

  return (
    <div>
      <dt className="flex items-center gap-1.5 text-overline uppercase text-[var(--text-tertiary)]">
        {label}
        <span className="tabular">{formatNumber(rows.length, locale)}</span>
      </dt>
      <dd className="mt-1.5">
        {rows.length === 0 ? (
          <span className="text-body-sm text-faint">—</span>
        ) : (
          <ul className="flex flex-wrap gap-2">
            {shown.map((row) => {
              const after = note?.(row);
              return (
                <li key={row.user.id} className="max-w-full">
                  <button
                    type="button"
                    aria-haspopup="dialog"
                    onClick={() =>
                      onOpen({
                        user: row.user,
                        dot,
                        doing: doing(row),
                        since: row.since,
                        alert: !!after?.alert,
                        workedMinutes: row.workedMinutes,
                      })
                    }
                    title={`${row.workedMinutes == null ? title(row) : `${title(row)} · ${worked(row)}`} — ${t('dash.workOpen')}`}
                    className="inline-flex max-w-full flex-col rounded-md bg-neutral-500/12 px-2.5 py-1 text-start text-body-sm ring-1 ring-inset ring-neutral-500/25 transition-colors duration-fast hover:bg-neutral-500/20 hover:ring-primary/50"
                  >
                    <span className="flex min-w-0 max-w-full items-center gap-1.5">
                      <StatusDot tone={dot} />
                      <span className="min-w-0 truncate text-[var(--text-primary)]">{localized(row.user)}</span>
                      {/* At the office, remote today by an approved request. */}
                      {row.user.remoteByRequest && (
                        <>
                          <RemoteIcon className="h-3 w-3 shrink-0 text-[var(--text-tertiary)]" aria-hidden />
                          <span className="sr-only">{t('remoteWork.todayByRequest')}</span>
                        </>
                      )}
                      {after && (
                        <span
                          className={clsx(
                            'whitespace-nowrap text-caption',
                            after.alert ? 'text-error-fg dark:text-error-dark' : 'text-muted',
                          )}
                        >
                          <span aria-hidden>· </span>
                          {after.text}
                        </span>
                      )}
                    </span>
                    {/* Under the name, past the dot: the hours really worked today. */}
                    {row.workedMinutes != null && (
                      <span className="flex items-center gap-1 ps-3.5 text-caption tabular text-muted">
                        <Clock className="h-3 w-3 shrink-0" aria-hidden />
                        <span className="sr-only">{`${t('attendance.worked')} `}</span>
                        {formatMinutes(row.workedMinutes, locale)}
                      </span>
                    )}
                    <span className="sr-only">{` — ${t('dash.workOpen')}`}</span>
                  </button>
                </li>
              );
            })}
            {more > 0 && (
              <li className="inline-flex items-center px-1 text-body-sm tabular text-muted">
                +{formatNumber(more, locale)}
              </li>
            )}
          </ul>
        )}
      </dd>
    </div>
  );
}

function StatusBars({ counts }: { counts: Record<string, number> }) {
  const { locale } = useI18n();
  const entries = Object.entries(counts ?? {});
  const max = Math.max(...entries.map(([, v]) => v), 1);
  if (entries.length === 0) return <p className="text-body-sm text-muted">—</p>;

  return (
    <ul className="space-y-2.5">
      {entries.map(([status, count]) => (
        <li key={status}>
          <div className="mb-1 flex items-baseline justify-between gap-3">
            <Badge tone={statusTone(status)}>{enumLabel(status, locale)}</Badge>
            <span className="text-body-sm tabular text-muted">{formatNumber(count, locale)}</span>
          </div>
          <ProgressBar percent={(count / max) * 100} tone="brand" />
        </li>
      ))}
    </ul>
  );
}
